import {reportError} from '@product-web/error/report'
import type {UserData} from '@product-web/user'
import {loadScript} from '@product-web/web-platform/load-script'

import config from 'config'

import type {FCWidgetConfig} from './types'

const FRESHCHAT_HOST = 'https://pleohelp.freshchat.com'

const CSAT_QUESTION = 'Did we resolve your issue?'

const getRestoreIdLocalStorageKey = (userId: string) => `pleo-fc-${userId}`

const saveRestoreId = ({userId, restoreId}: {userId?: string; restoreId: string}) => {
    if (!userId) {
        reportError('Freshchat: No User ID to store restoreID against')
        return
    }

    const LOCAL_STORAGE_KEY = getRestoreIdLocalStorageKey(userId)

    localStorage.setItem(LOCAL_STORAGE_KEY, restoreId)
}

const retrieveRestoreId = ({userId}: {userId?: string}) => {
    if (!userId) {
        reportError('Freshchat: No User ID to retreive restoreID against')
        return null
    }
    const LOCAL_STORAGE_KEY = getRestoreIdLocalStorageKey(userId)

    return localStorage.getItem(LOCAL_STORAGE_KEY) || null
}

const load = (onload: () => void) => {
    loadScript({
        src: `${FRESHCHAT_HOST}/js/widget.js`,
        asynchronous: true,
        id: 'freshchat-lib',
        onload,
    })
}

const isLoaded = () => window.fcWidget?.isLoaded()

const initialise = (user?: UserData) => {
    const externalId = user?.id

    const initConfig: FCWidgetConfig = {
        token: config.freshchat.token,
        host: FRESHCHAT_HOST,
        widgetUuid: config.freshchat.widgetId,
        firstName: user?.employee?.firstName,
        lastName: user?.employee?.lastName,
        restoreId: retrieveRestoreId({userId: externalId}),
        externalId,
        config: {
            headerProperty: {
                hideChatButton: true,
            },
            content: {
                headers: {
                    csat_question: CSAT_QUESTION,
                },
            },
        },
    }

    window.fcWidget?.init(initConfig)

    window.fcWidget?.on('user:created', (response: any) => {
        if (response?.data?.restoreId) {
            saveRestoreId({userId: externalId, restoreId: response.data.restoreId})
        }
    })
}

const open = () => window.fcWidget?.open()

const clearUser = async () => {
    if (!window.fcWidget?.isLoaded()) {
        return
    }
    await window.fcWidget?.user.clear()
}

export const Freshchat = {
    load,
    isLoaded,
    initialise,
    open,
    clearUser,
}
